import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { useUsers } from '../../user/UserContext';
import { PrintButton, FormatMessage } from '../core';
import { useSettings } from '../../mall_settings/SettingsProvider';
import {
    sumTotalCost,
    sumQuantity,
    sumValuesTotal,
    sumShipping,
    filterPhysical,
    filterDigital,
    sumTotalFees
} from '../../lineItem/lineItemSelectors';

import { ItemConfirmationList } from './ItemConfirmation';
import { orderConfirmationMessages } from './confirmationMessages';
import { ItemConfirmationSummary } from './ItemConfirmationSummary';
import { OrderConfirmationSummary } from './OrderConfirmationSummary';
import { SAPConfirmationSummary } from './SAPConfirmationSummary';

export const OrderConfirmationDetail = ({ lineItems, payment, orderNumber, statement, promotions }) => {
    const {userData} = useUsers();
    const { userPoints } = useSettings();
    const quantity = sumQuantity(lineItems);
    const { totalDiscountRewardAmount = 0 } = promotions;
    const grandTotal = sumTotalCost(lineItems) - totalDiscountRewardAmount;
    const eGiftLineItems = filterDigital(lineItems);
    const eGiftQuantity = sumQuantity(eGiftLineItems);
    const physicalLineItems = filterPhysical(lineItems);
    const physicalQuantity = sumQuantity(physicalLineItems);
    const confirmationEmail = userPoints ? userData.email : payment.purchaserEmail;

    const orderSummaryDetailProps = {
        orderNumber,
        orderTotalAmount: grandTotal,
        currency: 'USD'
    };
    if (!userPoints) {
        orderSummaryDetailProps.ccLastFour = payment.ccLastFour;
        orderSummaryDetailProps.ccStatementName = statement;
    }

    const itemSummaryProps = {
        quantity,
        total: grandTotal,
        subTotal: sumValuesTotal(lineItems),
        shipping: sumShipping(lineItems),
        discount: totalDiscountRewardAmount,
        fees: sumTotalFees(lineItems),
    };
    const printButtonProps = {
        printMessage: orderConfirmationMessages.printReceipt
    };
    return (
        <React.Fragment>
            <PrintButton {...printButtonProps} />
            {userPoints ? (
                <SAPConfirmationSummary {...orderSummaryDetailProps} />
            ) : (
                <OrderConfirmationSummary {...orderSummaryDetailProps} />
            )}
            <div className="order-confirmation-detail">
                <h2 className="order-confirmation-detail-heading">
                    <FormattedMessage {...orderConfirmationMessages.itemDetailsHeading} />
                </h2>
                {eGiftQuantity > 0 && (
                    <React.Fragment>
                        <h3 className="order-confirmation-detail-sub-heading">
                            <FormatMessage
                                intlMessage={orderConfirmationMessages.itemDetailsEgiftLabel}
                                values={{ eGiftQuantity }}
                            />
                        </h3>
                        <ItemConfirmationList
                            items={eGiftLineItems}
                            confirmationEmail={confirmationEmail}
                            promotions={promotions}
                        />
                    </React.Fragment>
                )}
                {physicalQuantity > 0 && (
                    <React.Fragment>
                        <h3 className="order-confirmation-detail-sub-heading">
                            <FormatMessage
                                intlMessage={orderConfirmationMessages.itemDetailsPhysicalLabel}
                                values={{ physicalQuantity }}
                            />
                        </h3>
                        <ItemConfirmationList
                            items={physicalLineItems}
                            confirmationEmail={confirmationEmail}
                            promotions={promotions}
                        />
                    </React.Fragment>
                )}
                <ItemConfirmationSummary {...itemSummaryProps} />
            </div>
        </React.Fragment>
    );
};

OrderConfirmationDetail.propTypes = {
    lineItems: PropTypes.array.isRequired,
    payment: PropTypes.object.isRequired,
    orderNumber: PropTypes.string.isRequired,
    statement: PropTypes.string.isRequired,
    promotions: PropTypes.shape({
        totalDiscountRewardAmount: PropTypes.number,
    }),
};

OrderConfirmationDetail.defaultProps = {
    promotions: {},
};
